import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useState, useMemo, useEffect } from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddIcon from "@mui/icons-material/Add";
import AddProduct from "../../Modals/addProduct";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { useGetProductListQuery, useDeleteProductMutation, useUpdateProductStatusMutation, useUpdateProductFeaturedMutation, ProductItem } from "../../store/api/manageAPI";
import { showToast, showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import { PRODUCT_TYPE, VARIANT_TYPE } from "../../constants/enums";

const ManageProducts = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [openModal, setOpenModal] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Fetch products from API
  const { data, isLoading, error, refetch } = useGetProductListQuery({
    page,
    limit,
    search: searchTerm,
  });

  // Delete product mutation
  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();

  // Update product status mutation
  const [updateProductStatus, { isLoading: isUpdatingStatus }] = useUpdateProductStatusMutation();

  // Update product featured mutation
  const [updateProductFeatured, { isLoading: isUpdatingFeatured }] = useUpdateProductFeaturedMutation();

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditProduct(null);
    refetch();
  };

  const handleEditClick = (product: any) => {
    // Find the full product data from the API response
    const fullProduct = data?.data?.product?.find((p: any) => (p._id || p.id) === product.id);
    if (fullProduct) {
      const productData: ProductItem = {
        _id: fullProduct._id || fullProduct.id,
        id: fullProduct._id || fullProduct.id,
        name: fullProduct.name,
        image: fullProduct.image,
        ingredients: fullProduct.ingredients,
        description: fullProduct.description,
        type: fullProduct.type,
        variant: fullProduct.variant,
        size: fullProduct.size,
        price: fullProduct.price,
        doublePrice: fullProduct.doublePrice,
        isActive: fullProduct.isActive,
        categoryDetails: fullProduct.categoryDetails,
        categoryId: fullProduct.categoryDetails?._id,
      };
      setEditProduct(productData);
      setOpenModal(true);
    }
  };

  const handleDeleteClick = (product: any) => {
    setProductToDelete({ id: product.id, name: product.name });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      const response = await deleteProduct({ id: productToDelete.id }).unwrap();
      const successMessage = response?.message || `Product "${productToDelete.name}" deleted successfully`;
      showToast(successMessage);
      setDeleteModalOpen(false);
      setProductToDelete(null);
      refetch();
    } catch (error: any) {
      console.error("Error deleting product:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete product. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle status toggle (isActive)
  const handleStatusToggle = async (productId: string, productName: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    
    try {
      const response = await updateProductStatus({ 
        id: productId, 
        isActive: newStatus 
      }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || 
        `Product "${productName}" ${newStatus ? "activated" : "deactivated"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating product status:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update product status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state if update failed
      refetch();
    }
  };

  // Handle featured toggle
  const handleFeaturedToggle = async (productId: string, productName: string, currentFeatured: boolean, currentIsActive: boolean) => {
    const newFeatured = !currentFeatured;
    
    try {
      const response = await updateProductFeatured({ 
        id: productId, 
        featured: newFeatured,
        isActive: currentIsActive
      }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || 
        `Product "${productName}" ${newFeatured ? "featured" : "unfeatured"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating product featured status:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update product featured status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state if update failed
      refetch();
    }
  };

  // Extract products from API response
  const products = useMemo(() => {
    if (data?.data?.product && Array.isArray(data.data.product)) {
      return data.data.product.map((product: any) => ({
        id: product._id || product.id,
        name: product.name || "N/A",
        description: product.description || "-",
        image: product.image || "/static/images/placeholder.png",
        category: product.categoryDetails?.name || "N/A",
        type: product.type === PRODUCT_TYPE.SIZE ? "size" : "variant",
        size: product.size ? `${product.size} Oz` : undefined,
        variant: product.variant === VARIANT_TYPE.SINGLE ? "Single Variant" : product.variant === VARIANT_TYPE.DOUBLE ? "Double Variant" : undefined,
        price: product.price ? `${product.price.toFixed(3)} OMR` : undefined,
        singlePrice: product.price ? `${product.price.toFixed(3)} OMR` : undefined,
        doublePrice: product.doublePrice ? `${product.doublePrice.toFixed(3)} OMR` : undefined,
        isActive: product.isActive ?? true,
        featured: product.featured ?? false,
        createdAt: product.createdAt
          ? new Date(product.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      }));
    }
    return [];
  }, [data]);

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [type, setType] = React.useState('default');
  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value as string);
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus || isUpdatingFeatured} />
      <div className="main_title">
        <h1>Manage Products</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Products
        </p>
      </div>

      {/* Table */}
      <div className="cards mt_20">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={type}
                  onChange={handleTypeChange}
                >
                  <MenuItem disabled value="default">Select Category</MenuItem>
                  <MenuItem value="1">Smothies</MenuItem>
                  <MenuItem value="2">Beverages</MenuItem>
                  <MenuItem value="3">Greek Bowl</MenuItem>
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditProduct(null);
                setOpenModal(true);
              }}
            >
              <AddIcon /> Add New Product
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Error loading products. Please try again.
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                products.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <div className="user_block no_radius">
                        <figure>
                          <img 
                            src={row.image?.startsWith('http') ? row.image : `/static/images/${row.image}`} 
                            alt={row.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/static/images/placeholder.png";
                            }}
                          />
                        </figure>
                        <p><b>{row.name}</b></p>
                      </div>
                    </TableCell>

                  <TableCell className="max_c2">
                    <p>{row.description || "-"}</p>
                  </TableCell>

                  <TableCell>{row.category}</TableCell>

                  {/* ---------------- TYPE COLUMN ---------------- */}
                  <TableCell>
                    {row.type === "size" && (
                      <>
                        <strong>Size:</strong> {row.size}
                      </>
                    )}
                    {row.type === "variant" && row.variant}
                  </TableCell>

                  {/* ---------------- PRICE COLUMN ---------------- */}
                  <TableCell>
                    {/* If TYPE is SIZE -> show only one price */}
                    {row.type === "size" && (
                      <span>{row.price}</span>
                    )}

                    {/* Single Variant */}
                    {row.type === "variant" && row.variant === "Single Variant" && (
                      <span>{row.price}</span>
                    )}

                    {/* Double Variant */}
                    {row.type === "variant" && row.variant === "Double Variant" && (
                      <>
                        <div><strong>Single:</strong> {row.singlePrice}</div>
                        <div><strong>Double:</strong> {row.doublePrice}</div>
                      </>
                    )}
                  </TableCell>

                    <TableCell>{row.createdAt}</TableCell>

                    <TableCell>
                      <SwitchToggle 
                        checked={row.featured} 
                        onChange={() => handleFeaturedToggle(row.id, row.name, row.featured, row.isActive)}
                        disabled={isUpdatingFeatured || isLoading}
                      />
                    </TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={row.isActive} 
                        onChange={() => handleStatusToggle(row.id, row.name, row.isActive)}
                        disabled={isUpdatingStatus || isLoading}
                      />
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton 
                          onClick={() => {
                            const fullProduct = data?.data?.product?.find((p: any) => (p._id || p.id) === row.id);
                            if (fullProduct) {
                              navigate("/manage-products/detail", { state: { product: fullProduct } });
                            }
                          }} 
                          disabled={isLoading}
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleEditClick(row)}
                          disabled={isLoading}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(row)}
                          disabled={isLoading || isDeleting}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div className="table_footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderTop: '1px solid #e0e0e0' }}>
            <p style={{ margin: 0, color: '#717171', fontSize: '14px' }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Pagination
                module={products}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <AddProduct
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editProduct={editProduct}
      />

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProductToDelete(null);
        }}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        itemName={productToDelete?.name || "this product"}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageProducts;
